// src/v3/ui/datasource/datasource.hooks.ts
//
// TanStack Query server-state layer for the datasource feature.
// All reads use useQuery, all mutations use useMutation with key-based
// invalidation. No ad-hoc useState+useEffect+apiFetch for server-state.
// SSE/events-stream for long-running operations (process-all) and view-state
// (UI flags, progress) in useState remain unchanged.

import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { symbolFilterSchema } from "@signals/shared";
import type { SymbolFilter } from "@signals/shared";
import { apiFetch, qk } from "../_common";
import { DATASOURCE_RESULT_STATUS } from "../_deps";
import type { FilterClause } from "../_deps";
import type { SymbolItem } from "../symbol";
export type { SymbolFilter } from "@signals/shared";

// ── Schemas (kept for type inference and runtime validation) ─────────────────

const importResultSchema = z.object({
  status: z.enum(DATASOURCE_RESULT_STATUS),
  imported: z.number().optional(),
  enrichmentFailures: z.number().optional(),
  symbolsProcessed: z.number().optional(),
  candlesUpserted: z.number().optional(),
  failed: z
    .array(z.object({ symbol: z.string(), error: z.string() }))
    .optional(),
  filteredOut: z
    .array(
      z.object({
        symbol: z.string(),
        name: z.string().nullable().optional(),
        reasons: z.array(z.string()),
      }),
    )
    .optional(),
  date: z.string().optional(),
  pageId: z.string().optional(),
  version: z.number().optional(),
  updated: z.boolean().optional(),
  successes: z.array(z.string()).optional(),
  failures: z.record(z.string(), z.string()).optional(),
  saved: z.number().optional(),
  filtered: z.number().optional(),
  error: z.string().optional(),
});

export type ImportResult = z.infer<typeof importResultSchema>;

const freshnessSchema = z.object({
  symbols: z.string().nullable(),
  candles: z.string().nullable(),
  priceHistory: z.string().nullable(),
  strategies: z.string().nullable(),
});

export type Freshness = z.infer<typeof freshnessSchema>;

const auditFilteredOutItemSchema = z.object({
  symbol: z.string(),
  name: z.string().nullable().optional(),
  reasons: z.array(z.string()),
});

const auditResultSchema = z.object({
  total: z.number(),
  passedCount: z.number(),
  filteredOut: z.array(auditFilteredOutItemSchema),
});

export type AuditResult = z.infer<typeof auditResultSchema>;
export type AuditFilteredOutItem = z.infer<typeof auditFilteredOutItemSchema>;

const applyAuditResultSchema = z.object({
  status: z.string(),
  deleted: z.number(),
  deletedPinbars: z.number(),
  deletedAnalyses: z.number(),
  deletedCandles: z.number(),
  deletedHistories: z.number(),
  deletedHistoryEntries: z.number(),
  items: z.array(z.string()),
});

export type ApplyAuditResult = z.infer<typeof applyAuditResultSchema>;

const processAllResultSchema = z.object({
  processed: z.number(),
  total: z.number(),
  failed: z.number(),
});

export type ProcessAllResult = z.infer<typeof processAllResultSchema>;

// ── Reads ───────────────────────────────────────────────────────────────────

/** Read: data freshness metrics. */
export function useFreshness() {
  return useQuery<Freshness, Error>({
    queryKey: qk.datasource.freshness,
    queryFn: async () => {
      const body = await apiFetch<unknown>("/api/datasource/freshness");
      return freshnessSchema.parse(body);
    },
  });
}

/** Read: symbol filters list. */
export function useSymbolFilters() {
  return useQuery<SymbolFilter[], Error>({
    queryKey: qk.datasource.symbolFilters,
    queryFn: async () => {
      const body = await apiFetch<{ filters: unknown[] }>(
        "/api/datasource/symbol-filters",
      );
      return z.array(symbolFilterSchema).parse(body.filters);
    },
  });
}

/** Read: symbols list with optional filters (used for detail modal lookup). */
export function useSymbolsList(filters?: {
  limit?: number;
  filters?: FilterClause[];
}) {
  const searchParams = new URLSearchParams();
  if (filters?.limit) searchParams.set("limit", String(filters.limit));
  if (filters?.filters && filters.filters.length > 0) {
    searchParams.set("filters", JSON.stringify(filters.filters));
  }
  const qs = searchParams.toString();
  return useQuery<{ items: SymbolItem[]; total: number }, Error>({
    queryKey: qk.symbols.list(filters ?? {}),
    queryFn: () =>
      apiFetch<{ items: SymbolItem[]; total: number }>(
        `/api/symbols${qs ? `?${qs}` : ""}`,
      ),
    enabled: Boolean(filters),
  });
}

// ── Mutations ───────────────────────────────────────────────────────────────

/** Mutation: import symbols from exchanges. Invalidates freshness + symbols.list. */
export function useImportSymbols() {
  const queryClient = useQueryClient();
  const mutation = useMutation<ImportResult, Error, string[]>({
    mutationFn: async (exchanges) => {
      const body = await apiFetch<unknown>("/api/datasource/import-symbols", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          exchanges: exchanges ?? ["NASDAQ", "NYSE"],
        }),
      });
      return importResultSchema.parse(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: qk.datasource.freshness,
      });
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
    },
    onError: () => {
      // error is exposed via mutation.error — no state to set
    },
  });
  return {
    isLoading: mutation.isPending,
    result: mutation.data ?? null,
    error: mutation.error?.message ?? null,
    run: mutation.mutate,
    reset: mutation.reset,
  };
}

/** Mutation: import candles for a date. Invalidates freshness + symbols.list. */
export function useImportCandles() {
  const queryClient = useQueryClient();
  const mutation = useMutation<ImportResult, Error, string>({
    mutationFn: async (date) => {
      const body = await apiFetch<unknown>("/api/datasource/import-candles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date }),
      });
      return importResultSchema.parse(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: qk.datasource.freshness,
      });
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
    },
  });
  return {
    isLoading: mutation.isPending,
    result: mutation.data ?? null,
    error: mutation.error?.message ?? null,
    run: mutation.mutate,
    reset: mutation.reset,
  };
}

/** Mutation: import price history for symbols. Invalidates freshness + symbols.list. */
export function useImportPriceHistory() {
  const queryClient = useQueryClient();
  const mutation = useMutation<ImportResult, Error, string[]>({
    mutationFn: async (symbols) => {
      const body = await apiFetch<unknown>(
        "/api/datasource/import-price-history",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ symbols }),
        },
      );
      return importResultSchema.parse(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: qk.datasource.freshness,
      });
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
    },
  });
  return {
    isLoading: mutation.isPending,
    result: mutation.data ?? null,
    error: mutation.error?.message ?? null,
    run: mutation.mutate,
    reset: mutation.reset,
  };
}

/** Mutation: run symbol audit. Returns audit result (no invalidation — read-like). */
export function useAuditSymbols() {
  const mutation = useMutation<AuditResult, Error, void>({
    mutationFn: async () => {
      const body = await apiFetch<unknown>("/api/datasource/audit-symbols", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      return auditResultSchema.parse(body);
    },
  });
  return {
    isLoading: mutation.isPending,
    result: mutation.data ?? null,
    error: mutation.error?.message ?? null,
    run: mutation.mutate,
    reset: mutation.reset,
  };
}

/** Mutation: apply audit (delete filtered-out symbols + cascades).
 *  Invalidates symbols.list + pinbar.list (cascade deletes) + freshness. */
export function useApplyAudit() {
  const queryClient = useQueryClient();
  const mutation = useMutation<
    ApplyAuditResult,
    Error,
    { symbols?: string[]; all?: boolean }
  >({
    mutationFn: async (params) => {
      const body = await apiFetch<unknown>("/api/datasource/apply-audit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });
      return applyAuditResultSchema.parse(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
      void queryClient.invalidateQueries({ queryKey: qk.pinbar.list() });
      void queryClient.invalidateQueries({
        queryKey: qk.datasource.freshness,
      });
    },
  });
  return {
    isLoading: mutation.isPending,
    result: mutation.data ?? null,
    error: mutation.error?.message ?? null,
    applyAudit: mutation.mutate,
    reset: mutation.reset,
  };
}

/** Mutation: update a single symbol filter. Invalidates symbolFilters. */
export function useUpdateSymbolFilter() {
  const queryClient = useQueryClient();
  const mutation = useMutation<SymbolFilter, Error, SymbolFilter>({
    mutationFn: async (filter) => {
      const body = await apiFetch<{ filter: unknown }>(
        `/api/datasource/symbol-filters/${filter.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(filter),
        },
      );
      return symbolFilterSchema.parse(body.filter);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: qk.datasource.symbolFilters,
      });
    },
  });
  return {
    isLoading: mutation.isPending,
    error: mutation.error?.message ?? null,
    update: mutation.mutate,
  };
}

/** Mutation: process a single ticker (from audit table).
 *  Invalidates symbols.list + freshness + symbol detail. */
export function useProcessTicker() {
  const queryClient = useQueryClient();
  const mutation = useMutation<unknown, Error, string>({
    mutationFn: (ticker) =>
      apiFetch("/api/symbols/process", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ symbols: [ticker] }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
      void queryClient.invalidateQueries({
        queryKey: qk.datasource.freshness,
      });
    },
  });
  return {
    isLoading: mutation.isPending,
    error: mutation.error?.message ?? null,
    run: mutation.mutate,
  };
}

/** Mutation: process all symbols (POST returns 202, progress via SSE).
 *  Invalidation happens in complete() when SSE reaches 100%. */
export function useProcessAllSymbols() {
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<ProcessAllResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation<unknown, Error, void>({
    mutationFn: () => apiFetch("/api/symbols/process-all", { method: "POST" }),
    onMutate: () => {
      setIsProcessing(true);
      setError(null);
      setResult(null);
    },
    onError: (err) => {
      setError(err.message);
      setIsProcessing(false);
    },
  });

  // Called by the page when SSE progress reaches 100%.
  const complete = useCallback(
    (r: ProcessAllResult) => {
      setResult(r);
      setIsProcessing(false);
      void queryClient.invalidateQueries({ queryKey: qk.symbols.list() });
      void queryClient.invalidateQueries({
        queryKey: qk.datasource.freshness,
      });
    },
    [queryClient],
  );

  // Called by the page on SSE error / cancellation.
  const fail = useCallback((msg: string) => {
    setError(msg);
    setIsProcessing(false);
  }, []);

  return {
    isLoading: isProcessing,
    result,
    error,
    run: mutation.mutate,
    complete,
    fail,
  };
}
